import { useEffect, useRef, useState } from 'react';
import { t, fmt } from '@/i18n';
import { getCsrfToken } from '@/lib/csrf';
import EmailField from '@/components/EmailField';
import type { RegistrationPayload, RegistrationResponse, RegistrationMode, AuthUserContext, PublicEventQuestion, AnswerPayload } from '../types';
import DynamicField from './DynamicField';

type AnswerValue = string | string[] | boolean | number;

interface RegistrationFormProps {
  eventSlug: string;
  registrationMode: RegistrationMode;
  isFull: boolean;
  onRegistered: (registration: RegistrationResponse) => void;
  authUser?: AuthUserContext | null;
  questions?: PublicEventQuestion[];
  spotsRemaining?: number | null;
  price?: string | null;
  currency?: string;
}

function buildAnswerPayload(
  questions: PublicEventQuestion[],
  answers: Record<number, AnswerValue>,
): AnswerPayload[] {
  const result: AnswerPayload[] = [];
  for (const q of questions) {
    const val = answers[q.id];
    if (val === undefined || val === '' || val === false) continue;

    if (q.field_type === 'multiselect') {
      const arr = Array.isArray(val) ? val : [];
      if (arr.length > 0) {
        result.push({ question: q.id, value_json: arr });
      }
    } else if (q.field_type === 'checkbox') {
      result.push({ question: q.id, value_text: val ? 'true' : 'false' });
    } else {
      result.push({ question: q.id, value_text: String(val) });
    }
  }
  return result;
}

function formatCurrency(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: 0 }).format(amount);
  } catch {
    return `${currency} ${amount.toLocaleString()}`;
  }
}

export default function RegistrationForm({ eventSlug, registrationMode, isFull, onRegistered, authUser, questions = [], spotsRemaining, price, currency = 'TWD' }: RegistrationFormProps) {
  const [formData, setFormData] = useState<RegistrationPayload>({
    full_name: authUser?.name ?? '',
    email: authUser?.email ?? '',
    phone: '',
    notes: '',
  });
  const [partySize, setPartySize] = useState(1);
  const [answers, setAnswers] = useState<Record<number, AnswerValue>>({});
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const formRef = useRef<HTMLFormElement>(null);
  useEffect(() => {
    if (Object.keys(errors).length > 0 && formRef.current) {
      const firstError = formRef.current.querySelector('.text-red-600, .text-red-400');
      if (firstError) {
        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const input = firstError.closest('div')?.querySelector<HTMLElement>('input, textarea, select');
        if (input) input.focus({ preventScroll: true });
      }
    }
  }, [errors]);

  const priceNum = price ? parseFloat(price) : 0;
  const hasPrice = priceNum > 0;
  const maxPartySize = spotsRemaining !== null && spotsRemaining !== undefined ? spotsRemaining : 99;
  const total = priceNum * partySize;

  function handleChange(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  }

  function handleEmailChange(value: string) {
    setFormData((prev) => ({ ...prev, email: value }));
    if (errors.email) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next.email;
        return next;
      });
    }
  }

  function handleAnswerChange(questionId: number, value: AnswerValue) {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
    if (errors.answers) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next.answers;
        return next;
      });
    }
  }

  function validateAnswers(): boolean {
    for (const q of questions) {
      if (!q.required) continue;
      const val = answers[q.id];
      if (val === undefined || val === '' || val === false) return false;
      if (q.field_type === 'multiselect' && Array.isArray(val) && val.length === 0) return false;
    }
    return true;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (!validateAnswers()) {
      setErrors((prev) => ({
        ...prev,
        answers: [t('Please fill in all required fields.')],
      }));
      return;
    }

    setSubmitting(true);
    setErrors({});

    try {
      const answerPayload = buildAnswerPayload(questions, answers);
      const response = await fetch(`/api/events/${eventSlug}/register/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCsrfToken(),
        },
        body: JSON.stringify({
          ...formData,
          party_size: partySize,
          ...(answerPayload.length > 0 ? { answers: answerPayload } : {}),
        }),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        if (typeof body === 'object' && body !== null) {
          const fieldErrors: Record<string, string[]> = {};
          for (const [key, value] of Object.entries(body)) {
            if (Array.isArray(value)) {
              fieldErrors[key] = value as string[];
            } else if (typeof value === 'string') {
              fieldErrors[key] = [value];
            }
          }
          setErrors(fieldErrors);
        }
        return;
      }

      const registration: RegistrationResponse = await response.json();
      onRegistered(registration);
    } finally {
      setSubmitting(false);
    }
  }

  const subtitleParts: string[] = [];
  if (hasPrice) {
    subtitleParts.push(`${formatCurrency(priceNum, currency)} ${t('per person')}`);
  } else {
    subtitleParts.push(t('Free'));
  }
  if (isFull) {
    subtitleParts.length = 0;
    subtitleParts.push(t('This event is at capacity. You can still register for the waitlist.'));
  } else if (registrationMode === 'approval_required') {
    subtitleParts.push(t('Registration requires organizer approval.'));
  } else if (!isFull) {
    subtitleParts.push(t('Instant confirmation'));
  }
  const subtitleText = subtitleParts.join(' · ');

  const isLowCapacity = spotsRemaining !== null && spotsRemaining !== undefined && spotsRemaining <= 3;

  const buttonText = submitting
    ? t('Submitting...')
    : isFull
      ? t('Join Waitlist')
      : partySize > 1
        ? fmt('Register for %(count)s people', { count: partySize })
        : t('Register');

  return (
    <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
      <div>
        <p className="text-xs text-gray-500 dark:text-gray-400">{subtitleText}</p>
      </div>

      {!authUser && eventSlug && (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t('Have an account?')}{' '}
          <a
            href={`/accounts/login/?next=/${eventSlug}/`}
            className="text-sm font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300"
          >
            {t('Log in')} &rarr;
          </a>
        </p>
      )}

      {errors.non_field_errors && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 dark:border-red-800 dark:bg-red-900/20" role="alert">
          {errors.non_field_errors.map((err, i) => (
            <p key={i} className="text-sm text-red-600 dark:text-red-400">{err}</p>
          ))}
        </div>
      )}

      {authUser ? (
        <div className="text-sm text-gray-600 dark:text-gray-400">
          <p>{fmt('Signing up as %(name)s', { name: authUser.name })}</p>
          <p className="text-gray-500 dark:text-gray-500">({authUser.email})</p>
          {errors.email && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.email[0]}</p>}
        </div>
      ) : (
        <>
          <div>
            <label htmlFor="full_name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('Full name')} <span className="text-red-500">*</span>
            </label>
            <input
              id="full_name"
              name="full_name"
              type="text"
              required
              value={formData.full_name}
              onChange={handleChange}
              className="mt-1 block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-teal-500 focus:ring-1 focus:ring-teal-500 dark:border-gray-700 dark:bg-gray-900 dark:text-white dark:placeholder:text-gray-500"
              placeholder={t('Your name')}
            />
            {errors.full_name && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.full_name[0]}</p>}
          </div>

          <EmailField
            id="email"
            name="email"
            label={t('Email')}
            required
            value={formData.email}
            onChange={handleEmailChange}
            error={errors.email?.[0]}
          />
        </>
      )}

      <div>
        <label htmlFor="phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          {t('Phone')}
        </label>
        <input
          id="phone"
          name="phone"
          type="tel"
          value={formData.phone}
          onChange={handleChange}
          className="mt-1 block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-teal-500 focus:ring-1 focus:ring-teal-500 dark:border-gray-700 dark:bg-gray-900 dark:text-white dark:placeholder:text-gray-500"
          placeholder={t('Optional')}
        />
        {errors.phone && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.phone[0]}</p>}
      </div>

      {/* Party Size Stepper */}
      {!isFull && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            {t('Number of people')} <span className="text-red-500">*</span>
          </label>
          <div className="mt-1 flex items-center gap-3">
            <button
              type="button"
              disabled={partySize <= 1}
              onClick={() => setPartySize((p) => Math.max(1, p - 1))}
              className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 hover:border-gray-400 transition-colors disabled:text-gray-400 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:border-gray-300 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-800 dark:disabled:text-gray-600"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M20 12H4" /></svg>
            </button>
            <span className="w-8 text-center text-sm font-semibold text-gray-900 dark:text-white">{partySize}</span>
            <button
              type="button"
              disabled={partySize >= maxPartySize}
              onClick={() => setPartySize((p) => Math.min(maxPartySize, p + 1))}
              className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 hover:border-gray-400 transition-colors disabled:text-gray-400 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:border-gray-300 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-800 dark:disabled:text-gray-600"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M12 4v16m8-8H4" /></svg>
            </button>
            {spotsRemaining !== null && spotsRemaining !== undefined && (
              <span className={`text-xs ${isLowCapacity ? 'font-medium text-amber-600 dark:text-amber-400' : 'text-gray-400 dark:text-gray-500'}`}>
                {isLowCapacity
                  ? fmt('Only %(count)s spots left', { count: spotsRemaining })
                  : fmt('%(count)s spots left', { count: spotsRemaining })}
              </span>
            )}
          </div>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{t('Including yourself')}</p>
          {errors.party_size && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.party_size[0]}</p>}
        </div>
      )}

      <div>
        <label htmlFor="notes" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          {t('Notes')}
        </label>
        <textarea
          id="notes"
          name="notes"
          rows={2}
          value={formData.notes}
          onChange={handleChange}
          className="mt-1 block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-teal-500 focus:ring-1 focus:ring-teal-500 dark:border-gray-700 dark:bg-gray-900 dark:text-white dark:placeholder:text-gray-500"
          placeholder={t('Anything the organizer should know?')}
        />
        {errors.notes && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.notes[0]}</p>}
      </div>

      {/* Dynamic custom questions */}
      {questions.length > 0 && (
        <>
          {questions.map((q) => (
            <DynamicField
              key={q.id}
              question={q}
              value={answers[q.id] ?? (q.field_type === 'multiselect' ? [] : q.field_type === 'checkbox' ? false : '')}
              onChange={handleAnswerChange}
            />
          ))}
        </>
      )}

      {errors.answers && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 dark:border-red-800 dark:bg-red-900/20">
          {errors.answers.map((err, i) => (
            <p key={i} className="text-sm text-red-600 dark:text-red-400">{err}</p>
          ))}
        </div>
      )}

      {/* Total display */}
      {hasPrice && !isFull && (
        <div className={`rounded-lg px-3 py-2 ${partySize > 1 ? 'bg-teal-50 border border-teal-100 dark:bg-teal-900/20 dark:border-teal-800' : 'bg-gray-50 dark:bg-gray-800/50'}`}>
          <div className="flex items-center justify-between text-sm">
            <span className={partySize > 1 ? 'text-teal-700 dark:text-teal-300' : 'text-gray-600 dark:text-gray-400'}>{t('Total')}</span>
            <span className={`font-semibold ${partySize > 1 ? 'text-teal-900 dark:text-teal-200' : 'text-gray-900 dark:text-white'}`}>
              {formatCurrency(total, currency)}
            </span>
          </div>
          <p className={`text-xs ${partySize > 1 ? 'text-teal-600 dark:text-teal-400' : 'text-gray-400 dark:text-gray-500'}`}>
            {formatCurrency(priceNum, currency)} &times; {partySize} {partySize === 1 ? t('person') : t('people')}
          </p>
        </div>
      )}

      <button
        type="submit"
        disabled={submitting}
        className="w-full rounded-xl bg-teal-600 px-6 py-3 text-base font-semibold text-white shadow-lg shadow-teal-500/30 transition-colors hover:bg-teal-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-500 disabled:opacity-50"
      >
        {buttonText}
      </button>
    </form>
  );
}
